import { message } from '@repo/ui';
import { translate, TranslationEntity } from '@repo/utils';
import { type NodeProps } from '@xyflow/react';
import {
  Button, Card, Flex, Radio,
} from 'antd';
import NodeMenu from 'automations/components/NodeMenu';
import { waitUnits } from 'automations/data/waitNode';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { getParentIdSelector } from 'automations/selectors/reactFlow';
import { addAdditionalOptions } from 'automations/store/reactFlowSlice';
import { nodeSchemas, type WaitNodeData, type WaitNodeType } from 'automations/types/nodeTypes';
import { saveConditionNode } from 'automations/utils/conditionNode';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ZodError } from 'zod';

import CustomWaitContent from './CustomWaitContent';
import ForWaitContent from './ForWaitContent';
import styles from './WaitNodeMenu.module.scss';

type WaitType = WaitNodeData['additionalOptions']['wait']['type'];
type ForDataType = Extract<WaitNodeData['additionalOptions']['wait'], { type: 'for' }>;

const initialForData: ForDataType = {
  type: 'for',
  for: {
    offset: 1,
    unit: waitUnits[0],
  },
};

export default function WaitNodeMenu({
  node,
}: {
  node: NodeProps<WaitNodeType>
}) {
  const dispatch = useAutomationDispatch();
  const parentId = useSelector(getParentIdSelector(node.id));
  const { setMenuOpen, contentMode } = useCommonNodeHandles(node);

  const [isLoading, setIsLoading] = useState(false);
  const [selectedType, setSelectedType] = useState<WaitType>(
    node.data?.additionalOptions?.wait?.type ?? 'for',
  );

  const [optionData, setOptionData] = useState<WaitNodeData['additionalOptions']['wait']>(
    node.data?.additionalOptions?.wait ?? initialForData,
  );

  const customCaseInitialData = useMemo(() => {
    if (optionData?.type === 'custom') {
      return optionData.custom;
    }
    return null;
  }, [optionData]);

  const forCaseInitialData = useMemo(() => {
    if (optionData?.type === 'for') {
      return optionData.for;
    }
    return null;
  }, [optionData]);

  const handleChange = useCallback((data: WaitNodeData['additionalOptions']['wait']) => {
    setOptionData(data);
  }, []);

  const handleSaveNode = useCallback(async () => {
    setIsLoading(true);

    try {
      const parsedNodeData = nodeSchemas.WaitNodeData.parse({
        ...node.data,
        additionalOptions: {
          wait: optionData,
        },
      });

      dispatch(addAdditionalOptions({
        nodeId: node.id,
        additionalOptions: parsedNodeData.additionalOptions,
      }));
      await dispatch(saveConditionNode(node.id, parentId, contentMode));
      message.success(translate(`#{${TranslationEntity.automation}} updated`, [{ capitalize: true }]));
      setMenuOpen(false);
    } catch (error: any) {
      const errorMessage = error instanceof ZodError
        ? error.issues.map((issue) => issue.message).join(', ')
        : error?.data?.message ?? translate(`Failed to update #{${TranslationEntity.automation}}`);
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, node.data, node.id, optionData, parentId, setMenuOpen, contentMode]);

  return (
    <NodeMenu
      node={node}
      drawerProps={{
        title: 'Condition - Wait',
      }}
      extraLarge={false}
    >
      <Card
        title={translate(`#{${TranslationEntity.lead}} waits`, [{ capitalize: true }])}
      >
        <Radio.Group
          onChange={(e) => setSelectedType(e.target.value)}
          value={selectedType}
          className={styles.radioGroup}
        >
          <Radio
            value="for"
            disabled={selectedType === 'for'}
          >
            <ForWaitContent
              disabled={selectedType !== 'for'}
              initialValue={forCaseInitialData ?? initialForData.for}
              onChange={(value) => handleChange({ type: 'for', for: value })}
            />
          </Radio>
          <Radio
            value="custom"
            disabled={selectedType === 'custom'}
          >
            <CustomWaitContent
              disabled={selectedType !== 'custom'}
              initialValue={customCaseInitialData ?? undefined}
              onChange={(value) => handleChange({ type: 'custom', custom: value })}
            />
          </Radio>
        </Radio.Group>
      </Card>
      <Flex justify="flex-end" gap={16} className={styles.bottomContent}>
        <Button
          type="primary"
          onClick={handleSaveNode}
          loading={isLoading}
        >
          Set Condition
        </Button>
      </Flex>
    </NodeMenu>
  );
}
